import type { BillingNote } from '@policyvision/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/hooks/useAuth';
import { DeleteBillingNoteDialog } from '@/billing/component/DeleteBillingNoteDialog';
import UpdateBillingNoteForm from '@/billing/component/UpdateBillingNoteForm';
import { useBillingNoteText } from '@/billing/hooks/useBillingNoteText';
import NoteCard from '@/common/components/card/NoteCard';
import { Button } from '@/common/components/ui/button';

type BillingNoteItemProps = {
  billingUuid: string;
  note: BillingNote;
};

export const BillingNoteItem = ({
  billingUuid,
  note,
}: BillingNoteItemProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const content = useBillingNoteText(note);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canModify = !isEditing && note.createdByUuid === user?.localAccountId;

  return (
    <>
      <NoteCard
        createdAt={note.createdAt}
        createdByName={note.createdByName}
        actions={
          canModify && (
            <div className="flex mt-0.5 gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-auto p-0 text-destructive font-normal"
                onClick={() => setIsEditing(true)}
              >
                {t('actions.edit')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-auto p-0 text-destructive font-normal"
                onClick={() => setIsDeleting(true)}
              >
                {t('actions.delete')}
              </Button>
            </div>
          )
        }
      >
        {isEditing ? (
          <UpdateBillingNoteForm
            billingUuid={billingUuid}
            note={note}
            onDone={() => setIsEditing(false)}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{content}</p>
        )}
      </NoteCard>
      {isDeleting && (
        <DeleteBillingNoteDialog
          billingUuid={billingUuid}
          noteUuid={note.uuid}
          onClose={() => setIsDeleting(false)}
        />
      )}
    </>
  );
};
